"use client";

/**
 * ListResultsDisplay - Display for array results
 *
 * Shows a list of items with count and optional pagination.
 * Used for tags, notifications, simple lists, etc.
 */

import { useState } from "react";
import { Tag } from "@/ui";
import { colors, spacing, textStyles, primitives } from "@/ui/tokens";
import { InboxOutlined, ExpandMore, FormatListBulleted } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolDivider,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface ListResult {
  success?: boolean;
  data?: unknown[];
  items?: unknown[];
  results?: unknown[];
  count?: number;
  total?: number;
  totalCount?: number;
  message?: string;
  [key: string]: unknown;
}

/**
 * Extract array data from result
 */
function extractItems(result: ListResult | unknown): unknown[] {
  if (!result || typeof result !== "object") return [];

  const r = result as ListResult;

  // Check common array properties
  if (Array.isArray(r.data)) return r.data;
  if (Array.isArray(r.items)) return r.items;
  if (Array.isArray(r.results)) return r.results;

  // If the result itself is an array
  if (Array.isArray(result)) return result;

  // Check for any array property
  for (const [key, value] of Object.entries(r)) {
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }

  return [];
}

/**
 * Get display text for an item
 */
function getItemText(item: unknown): { primary: string; secondary?: string } {
  if (typeof item === "string") {
    return { primary: item };
  }

  if (typeof item === "number") {
    return { primary: String(item) };
  }

  if (item && typeof item === "object") {
    const obj = item as Record<string, unknown>;

    // Try common name fields
    const name =
      obj.name ||
      obj.title ||
      obj.label ||
      obj.text ||
      obj.value ||
      obj.id;

    const description =
      obj.description ||
      obj.subtitle ||
      obj.message ||
      obj.email ||
      obj.count;

    return {
      primary: name ? String(name) : JSON.stringify(item),
      secondary: description ? String(description) : undefined,
    };
  }

  return { primary: JSON.stringify(item) };
}

const INITIAL_VISIBLE = 8;
const SCROLL_THRESHOLD = 30;

export function ListResultsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const toolState = deriveToolState(toolCall);

  const { status, isLoading, isError, error } = toolState;
  const { toolName, title } = toolCall;
  const result = toolCall.finalOutput as ListResult | undefined;

  const items = result ? extractItems(result) : [];
  const totalCount =
    result?.count ||
    result?.total ||
    result?.totalCount ||
    items.length;

  const needsTruncation = items.length > INITIAL_VISIBLE;
  const visibleItems = showAll ? items : items.slice(0, INITIAL_VISIBLE);
  const hiddenCount = items.length - INITIAL_VISIBLE;
  const useScrollContainer = showAll && items.length > SCROLL_THRESHOLD;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={title || ""}
        toolName={toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<FormatListBulleted style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {/* Loading state */}
        {isLoading && (
          <div style={{ padding: spacing[4] }}>
            <ToolProgress variant="dots" label="Loading items..." />
          </div>
        )}

        {/* Error state */}
        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {/* Results */}
        {!isLoading && !isError && (
          <>
            {/* Count header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacing[2],
                paddingLeft: spacing[4],
                paddingRight: spacing[4],
                paddingTop: spacing[3],
                paddingBottom: spacing[3],
                borderBottom: `1px solid ${colors.border.subtle}`,
                backgroundColor: primitives.neutral[50],
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: 500 }}>
                {totalCount} {totalCount === 1 ? "item" : "items"}
              </span>
              {items.length < totalCount && (
                <Tag variant="default" size="sm" color="neutral">
                  Showing {items.length}
                </Tag>
              )}
            </div>

            {/* Items list */}
            {items.length > 0 ? (
              <>
                <div
                  style={{
                    ...(useScrollContainer
                      ? { maxHeight: 320, overflowY: "auto" as const }
                      : {}),
                  }}
                >
                  {visibleItems.map((item, index) => {
                    const { primary, secondary } = getItemText(item);

                    return (
                      <div key={index}>
                        <div
                          style={{
                            paddingLeft: spacing[4],
                            paddingRight: spacing[4],
                            paddingTop: spacing[2],
                            paddingBottom: spacing[2],
                          }}
                        >
                          <span
                            style={{
                              ...textStyles.body2,
                              fontWeight: 500,
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {primary}
                          </span>
                          {secondary && (
                            <span
                              style={{
                                ...textStyles.caption,
                                color: colors.text.muted,
                                display: "block",
                              }}
                            >
                              {secondary}
                            </span>
                          )}
                        </div>
                        {index < visibleItems.length - 1 && (
                          <ToolDivider />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Show more / Show less toggle */}
                {needsTruncation && (
                  <button
                    onClick={() => setShowAll((prev) => !prev)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: spacing[1],
                      width: "100%",
                      padding: `${spacing[3]}px ${spacing[4]}px`,
                      border: "none",
                      borderTop: `1px solid ${colors.border.subtle}`,
                      background: primitives.neutral[50],
                      cursor: "pointer",
                      ...textStyles.caption,
                      fontWeight: 500,
                      color: colors.action.product,
                    }}
                  >
                    <ExpandMore
                      style={{
                        fontSize: 18,
                        transform: showAll ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                    {showAll
                      ? "Show less"
                      : `Show all ${items.length} items (+${hiddenCount} more)`}
                  </button>
                )}
              </>
            ) : (
              <ToolEmptyState icon={<InboxOutlined />} message="No items found" />
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default ListResultsDisplay;
